import React, { Suspense, useEffect, useState } from 'react'
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom'
import styled from 'styled-components'
import { Credentials, StringTranslations } from '@crowdin/crowdin-api-client'
import Popups from '../components/Popups'
import Web3ReactManager from '../components/Web3ReactManager'
import Swap from './Swap'
import { EN, allLanguages } from '../constants/localisation/languageCodes'
import { LanguageContext } from '../hooks/LanguageContext'
import { TranslationsContext } from '../hooks/TranslationsContext'

import Menu from '../components/Menu'

const AppWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  overflow-x: hidden;
`

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 32px 16px;
  align-items: center;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
  justify-content: center;
  background: ${({ theme }) =>
    theme.isDark
      ? `radial-gradient(circle at 20% 15%, rgba(4, 226, 255, 0.07), transparent 45%),
         radial-gradient(circle at 80% 10%, rgba(22, 83, 161, 0.10), transparent 50%),
         radial-gradient(circle at 50% 100%, rgba(4, 226, 255, 0.04), transparent 60%)`
      : `radial-gradient(circle at 20% 15%, rgba(4, 226, 255, 0.05), transparent 45%),
         radial-gradient(circle at 80% 10%, rgba(22, 83, 161, 0.06), transparent 50%)`};

  ${({ theme }) => theme.mediaQueries.lg} {
    min-height: 90vh;
  }
`

const Marginer = styled.div`
  margin-top: 5rem;
`

export default function App() {
  const [selectedLanguage, setSelectedLanguage] = useState<any>(undefined)
  const [translatedLanguage, setTranslatedLanguage] = useState<any>(undefined)
  const [translations, setTranslations] = useState<Array<any>>([])
  // Template-literal coercion turns an unset env var into the *string* "undefined",
  // which parseInt then turns into NaN - so an unconfigured build was calling
  // crowdin.com/api/v2/projects/NaN/... on every page load, 404ing, sending the
  // literal token "undefined" to a third party, and logging an error each time.
  const apiKey = process.env.REACT_APP_CROWDIN_APIKEY
  const projectId = Number.parseInt(process.env.REACT_APP_CROWDIN_PROJECTID ?? '', 10)
  const isCrowdinConfigured = Boolean(apiKey) && Number.isFinite(projectId)
  const fileId = 6

  const credentials: Credentials = {
    token: apiKey ?? '',
  }

  const stringTranslationsApi = new StringTranslations(credentials)

  const getStoredLang = (storedLangCode: string) => {
    return allLanguages.filter((language) => {
      return language.code === storedLangCode
    })[0]
  }

  useEffect(() => {
    const storedLangCode = localStorage.getItem('liberaDexLanguage')
    if (storedLangCode) {
      const storedLang = getStoredLang(storedLangCode)
      setSelectedLanguage(storedLang)
    } else {
      setSelectedLanguage(EN)
    }
  }, [])

  const fetchTranslationsForSelectedLanguage = async () => {
    // Without Crowdin credentials there is nothing to fetch. 'error' is this
    // component's existing signal to fall back to the bundled English strings.
    if (!isCrowdinConfigured) {
      setTranslations(['error'])
      setTranslatedLanguage(selectedLanguage)
      return
    }

    stringTranslationsApi
      .listLanguageTranslations(projectId, selectedLanguage.code, undefined, fileId, 200)
      .then((translationApiResponse) => {
        if (translationApiResponse.data.length < 1) {
          setTranslations(['error'])
        } else {
          setTranslations(translationApiResponse.data)
        }
      })
      .then(() => setTranslatedLanguage(selectedLanguage))
      .catch((error) => {
        setTranslations(['error'])
        console.error(error)
      })
  }

  useEffect(() => {
    if (selectedLanguage) {
      fetchTranslationsForSelectedLanguage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage])

  return (
    <Suspense fallback={null}>
      <HashRouter>
        <AppWrapper>
          <LanguageContext.Provider
            value={{ selectedLanguage, setSelectedLanguage, translatedLanguage, setTranslatedLanguage }}
          >
            <TranslationsContext.Provider value={{ translations, setTranslations }}>
              <Menu>
                <BodyWrapper>
                  <Popups />
                  <Web3ReactManager>
                    <Switch>
                      <Route exact strict path="/swap" component={Swap} />
                      {/* LiberaDex is swap-only by design - liquidity provision/pool
                          management is intentionally not part of the product surface.
                          Any other path (including the old /pool, /add, /remove/* routes) */}
                      <Route render={() => <Redirect to="/swap" />} />
                    </Switch>
                  </Web3ReactManager>
                  <Marginer />
                </BodyWrapper>
              </Menu>
            </TranslationsContext.Provider>
          </LanguageContext.Provider>
        </AppWrapper>
      </HashRouter>
    </Suspense>
  )
}
